import { SelectOption } from "@metsooutotec/modes-react-components"
import { getAllocatedSensors } from "../../stock-managment/stock-management-helper"
import { convertUnknownSiteNameToUndefined } from "../stock-allocation-helper"
import {
    AddStockAllocation,
    DeleteStockAllocation,
    EditStockAllocation,
    StockAllocationRow,
} from "features/iot/stock-management/models/stock-management-models"
import { Site } from "features/organizations/sites/models/sites-models"
import { StoreResults } from "stores/stores-helper"
import stockManagementStore from "features/iot/stock-management/stores/stock-management-store"
import { convertDateToDDMMMYYYY } from "components/utils/date-formatter-utils"

/**
 * Retrieves IDs of sensors allocated to a specific part number, filtered by customer and site.
 * Filters the allocated sensors by matching the provided customer ID and optionally the site ID.
 *
 * @param {string} partNumber - The part number of the sensors to find.
 * @param {string} customerId - The customer ID used to filter the sensors.
 * @param {string} [siteId] - (Optional) The site ID used to further filter the sensors. If not provided, only customer filtering will be applied.
 * @returns {string[]} - An array of sensor MAC addresses that match the part number, customer ID, and (if provided) site ID.
 */
export function getRelatedAllocatedSensorsMacAddress(partNumber: string, customerId: string, siteId?: string): string[] {
    const sensors = getAllocatedSensors(partNumber)
    const filteredSensors = sensors.filter(s => s.customerId === customerId && (s.siteId ?? "") === (siteId ?? ""))
    const sensorMacAddress = filteredSensors.map(s => s.macAddress)
    return sensorMacAddress
}

/**
 * Adjusts stock allocation based on the current and target quantities.
 * Adds or removes stock allocation as necessary.
 *
 * @param {number} currentQuantity - The current quantity of allocated sensors.
 * @param {number} targetQuantity - The target quantity of sensors to allocate.
 * @param {string} partNumber - The part number for the stock allocation.
 * @param {Date | null} allocationDate - The date of allocation.
 * @param {StockAllocationRow | null} stockAllocateRow - The stock allocation row being edited, if any.
 * @param {SelectOption | undefined} pickedCustomer - The customer selected for the stock allocation.
 * @param {SelectOption | undefined} pickedSite - The site selected for the stock allocation.
 * @returns {Promise<StoreResults>} - The result of the stock allocation adjustment.
 */
async function adjustStockAllocation(
    currentQuantity: number,
    targetQuantity: number,
    partNumber: string,
    allocationDate: Date | null,
    stockAllocateRow: StockAllocationRow | null,
    pickedCustomer: SelectOption | undefined,
    pickedSite: SelectOption | undefined,
): Promise<StoreResults> {
    try {
        if (!pickedCustomer?.value) return { message: "PickedCustomer Could not be empty!", passed: true }
        const difference = targetQuantity - currentQuantity
        if (difference > 0) {
            // Add Stock Allocation
            return await addStockAllocation(difference, partNumber, allocationDate, pickedCustomer, pickedSite ?? undefined)
        } else if (difference < 0) {
            // Remove Stock Allocation
            return await removeStockAllocation(difference, partNumber, stockAllocateRow, pickedCustomer?.value, pickedSite?.value)
        } else {
            // No adjustment needed
            return { message: "No stock allocation adjustment needed.", passed: true }
        }
    } catch {
        return { message: "Error occurred during stock allocation adjustment.", passed: false }
    }
}

/**
 * Removes stock allocation for a given quantity, part number, and stock allocation row.
 *
 * @param {number} difference - The quantity of stock to remove (expressed as a positive number).
 * @param {string} partNumber - The part number associated with the allocation to remove.
 * @param {StockAllocationRow | null} stockAllocateRow - The specific stock allocation row to adjust.
 * @returns {Promise<StoreResults>} - The result of the stock allocation removal.
 */
async function removeStockAllocation(
    difference: number,
    partNumber: string,
    stockAllocateRow: StockAllocationRow | null,
    customerId: string,
    siteId?: string,
): Promise<StoreResults> {
    const positiveNumber = Math.abs(difference)
    const existAllocatedSensorsMacAddress = getRelatedAllocatedSensorsMacAddress(partNumber, customerId, siteId)
    const sensorsToDelete = existAllocatedSensorsMacAddress.splice(0, positiveNumber)

    const deleteEntities: DeleteStockAllocation = { partNumber, listOfSensorsMacAddress: sensorsToDelete }
    return await stockManagementStore.deleteStockAllocation(deleteEntities)
}

/**
 * Adds stock allocation for a given quantity, part number, and selected customer and site.
 *
 * @param {number} difference - The quantity of stock to add.
 * @param {string} partNumber - The part number for the stock allocation.
 * @param {Date | null} allocationDate - The date of allocation.
 * @param {SelectOption | undefined} pickedCustomer - The customer selected for the stock allocation.
 * @param {SelectOption | undefined} pickedSite - The site selected for the stock allocation.
 * @returns {Promise<StoreResults>} - The result of the stock allocation addition.
 */
export async function addStockAllocation(
    difference: number,
    partNumber: string,
    allocationDate: Date | null,
    pickedCustomer: SelectOption | undefined,
    pickedSite: SelectOption | undefined,
): Promise<StoreResults> {
    try {
        const siteId = convertUnknownSiteNameToUndefined(pickedSite?.value) ?? undefined
        const addEntity: AddStockAllocation = {
            numberOfSensors: difference,
            partNumber,
            customerId: pickedCustomer?.value ?? "",
            siteId: siteId,
            allocationDate: convertDateToDDMMMYYYY(allocationDate ?? new Date()),
        }
        return await stockManagementStore.addStockAllocation(addEntity)
    } catch (e) {
        return { message: "Failed to add stock allocation.", passed: false }
    }
}

/**
 * Processes editing of stock allocation based on the editing status, part number, and other parameters.
 *
 * @param {boolean} isEditing - Flag indicating if the operation is for editing an existing allocation.
 * @param {string} partNumber - The part number involved in the stock allocation.
 * @param {StockAllocationRow | null} stockAllocateRow - The specific stock allocation row to edit.
 * @param {number} quantity - The target quantity for the stock allocation.
 * @param {Date | null} allocationDate - The date of allocation.
 * @param {SelectOption | undefined} pickedCustomer - The customer selected for the stock allocation.
 * @param {SelectOption | undefined} pickedSite - The site selected for the stock allocation.
 * @returns {Promise<StoreResults | null>} - The result of the editing process, or null if not editing.
 */
export async function editingProcess(
    isEditing: boolean,
    partNumber: string,
    stockAllocateRow: StockAllocationRow | null,
    quantity: number,
    allocationDate: Date | null,
    pickedCustomer: SelectOption | undefined,
    pickedSite: SelectOption | undefined,
): Promise<StoreResults | null> {
    try {
        if (!isEditing || !pickedCustomer?.value) return null

        const newSiteId = convertUnknownSiteNameToUndefined(pickedSite?.value)
        const existAllocatedSensorsMacAddress = getRelatedAllocatedSensorsMacAddress(partNumber, pickedCustomer?.value, pickedSite?.value)

        const editEntity: EditStockAllocation = {
            listOfSensorsMacAddress: existAllocatedSensorsMacAddress ?? [],
            newAllocationDate: convertDateToDDMMMYYYY(allocationDate ?? new Date()),
            newCustomerId: pickedCustomer?.value ?? "",
            newSiteId: newSiteId,
            partNumber: partNumber,
        }
        const result = await stockManagementStore.updateStockAllocation(editEntity)
        if (existAllocatedSensorsMacAddress.length !== quantity) {
            await adjustStockAllocation(
                existAllocatedSensorsMacAddress.length,
                quantity,
                partNumber,
                allocationDate,
                stockAllocateRow,
                pickedCustomer,
                pickedSite,
            )
        }

        return result
    } catch (e) {
        return { message: "Failed to edit stock allocation.", passed: false }
    }
}

/**
 * Ensures that an "Unknown" site exists in the given list of sites. If the list is null or undefined,
 * it initializes the list with the "Unknown" site. If the list does not contain an "Unknown" site,
 * it adds one to the list.
 *
 * @param {Array|null|undefined} sites - The current array of site objects, or null/undefined if no sites are defined.
 * @returns {Array} - The array of site objects, guaranteed to include an "Unknown" site.
 */
export function ensureUnknownSiteExists(sites: Site[] | undefined): Site[] {
    // Check if the array is undefined or if it doesn't already include an "Unknown" site.
    const unknownSiteExists = sites?.some(site => site.name.toLowerCase() === "unknown")

    // If sites is undefined or doesn't contain an "Unknown" site, return a new array with the "Unknown" site added.
    if (!unknownSiteExists) {
        const unknownSite = {
            name: "Unknown",
            id: "",
            assets: [],
            utcOffsetHours: "",
            metsoContact: "",
        }

        // Array spreading to include existing sites (if any) and add the "Unknown" site.
        return [...(sites || []), unknownSite]
    }

    // If the sites array is defined and already includes an "Unknown" site, return it as is.
    return sites || []
}

/**
 * Filters sites to exclude those with null name or id, and ensures an "Unknown" site exists.
 * Returns site options suitable for selection inputs.
 *
 * @param {Site[] | undefined} sites - Array of site objects to be filtered and mapped.
 * @returns {SelectOption[]} - Mapped and filtered site options with text and value properties.
 */
export function getFilteredSiteOptions(sites: Site[] | undefined): SelectOption[] {
    // Ensure an "Unknown" site exists and then filter and map to SelectOption format
    const filteredSites = ensureUnknownSiteExists(sites)
        .filter(s => s.name !== null && s.id !== null)
        .map(s => ({ text: s.name, value: s.id }))

    return filteredSites
}
